// StatusChip - the single status pill for the whole Timesheet app (ported from
// AIMS/Hire). One component covers every status domain; switch on `domain` -> tone.
// Renders an inline pill using CSS-variable tokens so it reacts to light/dark
// automatically. Always renders a text label (never colour alone) for a11y.
//
//   <StatusChip domain="approval" value="APPROVED" dot />
//   <StatusChip domain="usertype" value="Payroll" />
//
// Domains:
//   approval  -> PENDING / APPROVED / REJECTED task-approval states
//   usertype  -> PAYROLL / CONSULTANT user classification

import React from 'react';

// tone -> {color token, background token}
const TONES = {
  pass: { color: 'var(--pass)', bg: 'var(--pass-bg)' },
  fail: { color: 'var(--fail)', bg: 'var(--fail-bg)' },
  warn: { color: 'var(--warn)', bg: 'var(--warn-bg)' },
  info: { color: 'var(--info)', bg: 'var(--info-bg)' },
  neutral: { color: 'var(--neutral)', bg: 'var(--neutral-bg)' },
  primary: { color: 'var(--primary)', bg: 'var(--primary-tint)' },
};

const norm = (v) => String(v ?? '').trim().toUpperCase().replace(/[\s-]+/g, '_');

// domain -> (normalisedValue -> tone)
const DOMAIN_TONE = {
  approval: {
    APPROVED: 'pass',
    PENDING: 'warn',
    UNAPPROVED: 'warn',
    AWAITING_APPROVAL: 'warn',
    REJECTED: 'fail',
    DECLINED: 'fail',
  },
  usertype: {
    PAYROLL: 'info',
    CONSULTANT: 'primary',
  },
};

function humanLabel(value) {
  const n = norm(value);
  const words = n.toLowerCase().split('_');
  return words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

export default function StatusChip({
  value,
  domain = 'approval',
  label,
  dot = false,
  size = 'sm', // 'sm' | 'md'
  style,
  ...rest
}) {
  if (value === null || value === undefined || value === '') return null;

  const n = norm(value);
  const tone = (DOMAIN_TONE[domain] && DOMAIN_TONE[domain][n]) || 'neutral';
  const { color, bg } = TONES[tone] || TONES.neutral;
  const text = label != null ? label : humanLabel(value);

  const pad = size === 'md' ? '5px 11px' : '3px 9px';
  const fontSize = size === 'md' ? 11 : 9.5;

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        color,
        background: bg,
        padding: pad,
        borderRadius: 'var(--r-pill)',
        fontSize,
        fontWeight: 600,
        lineHeight: 1.4,
        letterSpacing: '.2px',
        whiteSpace: 'nowrap',
        ...style,
      }}
      {...rest}
    >
      {dot && <span style={{ fontSize: '0.85em', lineHeight: 1 }}>●</span>}
      {text}
    </span>
  );
}
